import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, PhoneIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
import { PHONE_DISPLAY, PHONE_HREF } from '../data/content';
interface LegalModalProps {
  document: LegalDocument | null;
  onClose: () => void;
}
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (!doc) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.document.body.style.overflow = previousOverflow;
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-150" data-mp="1ctr7ohx7q9sa">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-151" data-mp="1cspk5dz0futo">
          <motion.div className="absolute inset-0 bg-ink-900/70" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} aria-hidden="true" data-id="element-152" data-mp="1apexgww7ed0o" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby={`legal-${doc.id}-title`} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-lg bg-white shadow-2xl sm:rounded-lg" data-id="element-153" data-mp="1pmxq4ac05ade">
            <header className="flex items-start justify-between gap-4 border-b border-forest-100 bg-forest-800 px-6 py-5" data-id="element-154" data-mp="97mpj8f0ct39">
              <div data-id="element-155" data-mp="67ck9uumdgrg">
                <h2 id={`legal-${doc.id}-title`} className="font-display text-2xl uppercase leading-tight text-white" data-id="element-156" data-mp="66t3ihgpgyf0">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/60" data-id="element-157" data-mp="fx4f8q6msbwg">
                  {doc.updated}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/20 text-white transition-colors duration-150 ease-out hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-500" data-id="element-158" data-mp="3jduw603r49cx">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-159" data-mp="18m33m51ckaa7" />
              </button>
            </header>

            <div className="overflow-y-auto px-6 py-6" data-id="element-160" data-mp="13nrujwivm3t1">
              <p className="text-base leading-relaxed text-ink-700" data-id="element-161" data-mp="13pcopl1a3kqd">{doc.intro}</p>

              <div className="mt-6 space-y-6" data-id="element-162" data-mp="1b56j0bg42ae0">
                {doc.sections.map(section => <section key={section.heading} data-id="element-163" data-mp="1g7f9901lmycf">
                    <h3 className="font-display text-lg uppercase text-forest-800" data-id="element-164" data-mp="126l18iigqzea">
                      {section.heading}
                    </h3>
                    <div className="mt-2 space-y-2" data-id="element-165" data-mp="167y88vxal8ms">
                      {section.body.map(paragraph => <p key={paragraph} className="text-base leading-relaxed text-ink-700" data-id="element-166" data-mp="1tzbzeyjpiy66">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>

            <footer className="flex flex-col gap-3 border-t border-forest-100 bg-forest-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-167" data-mp="1p4pqd213lhp9">
              <p className="text-sm text-ink-700" data-id="element-168" data-mp="2hnd0jmek3cbk">Questions about this policy? Just call us.</p>
              <a href={PHONE_HREF} className="inline-flex items-center justify-center gap-2 rounded-md bg-ember-500 px-5 py-2.5 font-display text-base uppercase tracking-wide text-ink-900 transition-colors duration-150 ease-out hover:bg-ember-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-800" data-id="element-169" data-mp="tz1j51ynujnp">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-170" data-mp="g13i0y9cojq5" />
                {PHONE_DISPLAY}
              </a>
            </footer>
          </motion.div>
        </div>}
    </AnimatePresence>;
}